import { Either, isLeft } from "fp-ts/Either";
import * as t from "io-ts";
import { PathReporter } from "io-ts/PathReporter";
import React from "react";

/**
 * Return the React props for the given intrinsic element. Useful for building
 * components that are wrappers around those intrinsic elements. For example:
 *
 * ```ts
 * interface IProps extends IntrinsicElementProps<HTMLImageElement> {
 * }
 * ```
 */
export type IntrinsicElementProps<T = HTMLElement> = React.DetailedHTMLProps<
    React.HTMLAttributes<T>,
    T
>;

/**
 * Take a Wagtail ChoiceBlock and return all of the truthy values (not empty string,
 * undefined, or null).
 */
export type ChoiceBlockValues<ChoiceBlock> = Exclude<
    ChoiceBlock,
    "" | undefined | null
>;

/**
 * Response data from the csrf token endpoint
 */
export const CSRFResponse = t.type({
    csrf_token: t.string,
});
export type CSRFResponse = t.TypeOf<typeof CSRFResponse>;

/**
 * Response data from the csrf token endpoint
 */
export const FormValidationErrorsResponse = t.record(
    t.string,
    t.array(t.string),
);
export type FormValidationErrorsResponse = t.TypeOf<
    typeof FormValidationErrorsResponse
>;

/**
 * Helper function for type checking API response data via io-ts.
 */
export const check = <T>(result: Either<t.Errors, T>): T => {
    if (isLeft(result)) {
        const msg = PathReporter.report(result).join("\n\n");
        console.log(msg);
        throw new Error(msg);
    }
    return result.right;
};

/**
 * Given an object type, return a union of all of the keys which are considered
 * optional.
 */
export type OptionalKeys<T> = {
    [K in keyof T]-?: NonNullable<unknown> extends Pick<T, K> ? K : never;
}[keyof T];

/**
 * Given an object type, return a union of all of the keys which are considered
 * required.
 */
export type RequiredKeys<T> = {
    [K in keyof T]-?: NonNullable<unknown> extends { [P in K]: T[K] }
        ? never
        : K;
}[keyof T];

/**
 * Given an object type, invert the optionality of each of its properties. In
 * other words, each optional property becomes required and each required
 * property becomes optional.
 */
export type InvertOptional<T> = Required<Pick<T, OptionalKeys<T>>> &
    Partial<Omit<T, OptionalKeys<T>>> extends infer O
    ? { [K in keyof O]: O[K] }
    : never;

/**
 * Given a tuple type, return the type of all the elements excepts the first
 * element. Useful, for example, to get all function arguments except the first
 * argument.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type DropFirst<T extends unknown[]> = T extends [any, ...infer U]
    ? U
    : never;
